import React, { useState, useEffect } from 'react';

const ESATileDashboard = ({ reportData, onExportPDF }) => {
  const [isLoading, setIsLoading] = useState(true);
  
  // Sample report data - will be replaced with actual data from API
  const defaultReportData = {
    company_name: "Executive Manufacturing Co.",
    industry: "Food & Beverage",
    facility_size: "Medium (50,000-200,000 sq ft)",
    analysis_date: new Date().toLocaleDateString('en-US', { 
      year: 'numeric', month: 'long', day: 'numeric' 
    }),
    total_annual_savings: 1172804,
    opportunities_count: 15,
    energy_saved_kwh: 4104814,
    peak_demand_kw: 175920.6,
    payback_years: 0.4,
    roi_percentage: 118465,
    portfolio_roi: 654,
    three_year_value: 3518412,
    five_year_value: 5864020,
    efficiency_score: 62,
    priority_opportunities: [
      {
        id: 1,
        measure: "Contract Wood Pallet Recycling Company",
        annual_savings: 115740,
        payback: 0.03,
        details: "ROI: 3,254% | Waste Management"
      },
      {
        id: 2,
        measure: "Apply For Tax-Free Status Energy Purchases",
        annual_savings: 47840,
        payback: 0.05,
        details: "ROI: 1,960% | Regulatory"
      },
      {
        id: 3,
        measure: "Install Uninterruptible Power Supply",
        annual_savings: 551583,
        payback: 0.10,
        details: "ROI: 1,033% | Power Systems"
      },
      {
        id: 4,
        measure: "LED Lighting Conversion Program",
        annual_savings: 28450,
        payback: 0.45,
        details: "ROI: 222% | Lighting Systems"
      },
      {
        id: 5,
        measure: "HVAC System Optimization Controls",
        annual_savings: 65200,
        payback: 0.43,
        details: "ROI: 233% | HVAC Systems"
      }
    ]
  };

  const data = reportData || defaultReportData;

  useEffect(() => {
    // Simulate loading
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Generating your tile-based ESA report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 py-4 px-6 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">ESA Opportunity Report</h1>
            <p className="text-slate-600">Tile-Based Executive Dashboard</p>
          </div>
          <button
            onClick={onExportPDF}
            className="bg-emerald-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-emerald-700 transition-colors"
          >
            📄 Export to PDF
          </button>
        </div>
      </div>

      {/* Tile Dashboard Grid */}
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        
        {/* Cover Tile */}
        <CoverTile data={data} />
        
        {/* Executive Summary Tile */}
        <ExecutiveSummaryTile data={data} />
        
        {/* Priority Opportunities Tile */}
        <PriorityOpportunitiesTile data={data} />
        
        {/* Full Opportunity List Tile */}
        <FullOpportunityListTile data={data} />
        
        {/* Financial Analysis Tile */}
        <FinancialAnalysisTile data={data} />
        
        {/* Benchmarking Tile */}
        <BenchmarkingTile data={data} />
        
        {/* Implementation Roadmap Tile */}
        <ImplementationRoadmapTile data={data} />
        
        {/* LEAP CTA Tile */}
        <LEAPCTATile data={data} />
        
        {/* Methodology Tile */}
        <MethodologyTile data={data} />

      </div>
    </div>
  );
};

// Cover Tile Component
const CoverTile = ({ data }) => (
  <div className="bg-slate-900 rounded-xl shadow-lg overflow-hidden">
    <div className="p-8 text-center text-white">
      {/* Rely Ability Logo Area */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-white">RELY ABILITY</h2>
        <p className="text-emerald-300 text-lg">Energy Productivity Intelligence</p>
      </div>
      
      {/* Client Information */}
      <div className="bg-slate-800 rounded-lg p-6 mb-6 text-left">
        <h3 className="text-xl font-bold text-emerald-300 mb-4">Prepared for:</h3>
        <div className="space-y-2">
          <p><strong>Company:</strong> {data.company_name}</p>
          <p><strong>Industry:</strong> {data.industry}</p>
          <p><strong>Facility:</strong> {data.facility_size}</p>
          <p><strong>Report Date:</strong> {data.analysis_date}</p>
        </div>
      </div>
      
      {/* Badge */}
      <div className="inline-block bg-emerald-600 text-white px-6 py-3 rounded-lg font-bold text-lg">
        Paid Detailed Report – $990
      </div>
    </div>
  </div>
);

// Executive Summary Tile Component
const ExecutiveSummaryTile = ({ data }) => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden">
    <div className="bg-emerald-700 text-white p-6">
      <div className="flex items-center mb-4">
        <span className="text-2xl mr-3">📊</span>
        <h2 className="text-xl font-bold">Executive Summary</h2>
      </div>
    </div>
    
    {/* Large Savings Tile */}
    <div className="bg-emerald-600 text-white p-8 text-center">
      <div className="text-4xl font-bold mb-2">
        ${data.total_annual_savings.toLocaleString()}
      </div>
      <div className="text-emerald-100 text-lg">
        from {data.opportunities_count} recommended measures
      </div>
    </div>
    
    {/* KPI Tiles Grid */}
    <div className="p-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
      <div className="bg-emerald-100 rounded-lg p-4 text-center">
        <div className="text-2xl font-bold text-emerald-700">
          {data.energy_saved_kwh.toLocaleString()}
        </div>
        <div className="text-sm text-slate-600 mt-1">Energy Saved (kWh/year)</div>
      </div>
      
      <div className="bg-blue-100 rounded-lg p-4 text-center">
        <div className="text-2xl font-bold text-blue-700">
          {data.peak_demand_kw.toLocaleString()}
        </div>
        <div className="text-sm text-slate-600 mt-1">Peak Demand (kW)</div>
      </div>
      
      <div className="bg-slate-100 rounded-lg p-4 text-center">
        <div className="text-2xl font-bold text-slate-700">
          {data.payback_years}
        </div>
        <div className="text-sm text-slate-600 mt-1">Payback (years)</div>
      </div>
      
      <div className="bg-emerald-100 rounded-lg p-4 text-center">
        <div className="text-2xl font-bold text-emerald-700">
          {data.roi_percentage.toLocaleString()}%
        </div>
        <div className="text-sm text-slate-600 mt-1">ROI (%)</div>
      </div>
    </div>
  </div>
);

// Priority Opportunities Tile Component
const PriorityOpportunitiesTile = ({ data }) => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden">
    <div className="bg-slate-800 text-white p-6">
      <div className="flex items-center">
        <span className="text-2xl mr-3">💡</span>
        <h2 className="text-xl font-bold">Priority Opportunities</h2>
      </div>
    </div>
    
    <div className="p-6">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-100">
              <th className="text-left p-4 font-bold text-slate-800">#</th>
              <th className="text-left p-4 font-bold text-slate-800">Measure</th>
              <th className="text-left p-4 font-bold text-slate-800">Annual Savings</th>
              <th className="text-left p-4 font-bold text-slate-800">Payback</th>
              <th className="text-left p-4 font-bold text-slate-800">Details</th>
            </tr>
          </thead>
          <tbody>
            {data.priority_opportunities.map((opp, index) => (
              <tr key={opp.id} className={index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                <td className="p-4">
                  <div className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold">
                    {opp.id}
                  </div>
                </td>
                <td className="p-4 text-slate-800">
                  {opp.measure.length > 40 ? opp.measure.substring(0, 40) + "..." : opp.measure}
                </td>
                <td className="p-4 font-bold text-emerald-600">
                  ${opp.annual_savings.toLocaleString()}
                </td>
                <td className="p-4 text-slate-600">
                  {opp.payback}y
                </td>
                <td className="p-4 text-sm text-slate-600">
                  {opp.details}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// Full Opportunity List Tile Component (showing more opportunities)
const FullOpportunityListTile = ({ data }) => {
  const additionalOpportunities = [
    { measure: "Variable Speed Drive Implementation", savings: 42300, payback: 0.58, cost: 24500, partner: "Industrial Automation" },
    { measure: "Compressed Air System Optimization", savings: 18600, payback: 0.53, cost: 9800, partner: "Pneumatic Systems" },
    { measure: "Waste Heat Recovery Implementation", savings: 78400, payback: 0.57, cost: 45000, partner: "Thermal Systems Ltd" },
  ];

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="bg-slate-800 text-white p-6">
        <div className="flex items-center">
          <span className="text-2xl mr-3">📋</span>
          <h2 className="text-xl font-bold">Complete Opportunity Analysis</h2>
        </div>
      </div>
      
      <div className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-100">
                <th className="text-left p-4 font-bold text-slate-800">Measure</th>
                <th className="text-left p-4 font-bold text-slate-800">Savings</th>
                <th className="text-left p-4 font-bold text-slate-800">Payback</th>
                <th className="text-left p-4 font-bold text-slate-800">Cost</th>
                <th className="text-left p-4 font-bold text-slate-800">Partner</th>
              </tr>
            </thead>
            <tbody>
              {/* Priority opportunities first */}
              {data.priority_opportunities.map((opp, index) => (
                <tr key={`priority-${index}`} className={index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                  <td className="p-4 text-slate-800">{opp.measure}</td>
                  <td className="p-4 font-bold text-emerald-600">${opp.annual_savings.toLocaleString()}</td>
                  <td className="p-4 text-slate-600">{opp.payback}y</td>
                  <td className="p-4 text-slate-600">${(opp.annual_savings * opp.payback).toLocaleString()}</td>
                  <td className="p-4 text-blue-600">Energy Specialists</td>
                </tr>
              ))}
              
              {/* Additional opportunities */}
              {additionalOpportunities.map((opp, index) => (
                <tr key={`additional-${index}`} className={(data.priority_opportunities.length + index) % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                  <td className="p-4 text-slate-800">{opp.measure}</td>
                  <td className="p-4 font-bold text-emerald-600">${opp.savings.toLocaleString()}</td>
                  <td className="p-4 text-slate-600">{opp.payback}y</td>
                  <td className="p-4 text-slate-600">${opp.cost.toLocaleString()}</td>
                  <td className="p-4 text-blue-600">{opp.partner}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Financial Analysis Tile Component
const FinancialAnalysisTile = ({ data }) => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden">
    <div className="bg-slate-800 text-white p-6">
      <div className="flex items-center">
        <span className="text-2xl mr-3">📊</span>
        <h2 className="text-xl font-bold">Financial Analysis</h2>
      </div>
    </div>
    
    <div className="p-6">
      {/* Chart Placeholders */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-slate-100 rounded-lg p-6 text-center">
          <div className="text-4xl mb-2">🥧</div>
          <h3 className="font-bold text-slate-800 mb-2">Savings by Category</h3>
          <p className="text-sm text-slate-600">HVAC: 35% | Lighting: 25% | Motors: 20% | Other: 20%</p>
        </div>
        
        <div className="bg-slate-100 rounded-lg p-6 text-center">
          <div className="text-4xl mb-2">📊</div>
          <h3 className="font-bold text-slate-800 mb-2">Investment vs Savings</h3>
          <p className="text-sm text-slate-600">Investment: $180K | Annual Savings: $1.17M</p>
        </div>
        
        <div className="bg-slate-100 rounded-lg p-6 text-center">
          <div className="text-4xl mb-2">📈</div>
          <h3 className="font-bold text-slate-800 mb-2">Cumulative Impact</h3>
          <p className="text-sm text-slate-600">3-Year: ${data.three_year_value.toLocaleString()}</p>
        </div>
      </div>
      
      {/* Financial Callout Panel */}
      <div className="bg-emerald-100 rounded-lg p-6 border-l-4 border-emerald-600">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-emerald-700">{data.portfolio_roi}%</div>
            <div className="text-sm text-slate-600">Portfolio ROI</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-emerald-700">${data.three_year_value.toLocaleString()}</div>
            <div className="text-sm text-slate-600">3-Year Value</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-emerald-700">${data.five_year_value.toLocaleString()}</div>
            <div className="text-sm text-slate-600">5-Year Value</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Benchmarking Tile Component
const BenchmarkingTile = ({ data }) => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden">
    <div className="bg-slate-800 text-white p-6">
      <div className="flex items-center">
        <span className="text-2xl mr-3">🎯</span>
        <h2 className="text-xl font-bold">Industry Benchmarking</h2>
      </div>
    </div>
    
    <div className="p-6">
      {/* Horizontal Bar Chart Simulation */}
      <div className="mb-6">
        <h3 className="font-bold text-slate-800 mb-4">Performance Comparison</h3>
        
        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Your Facility</span>
              <span>68th percentile</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3">
              <div className="bg-amber-500 h-3 rounded-full" style={{width: '68%'}}></div>
            </div>
          </div>
          
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Industry Average</span>
              <span>50th percentile</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3">
              <div className="bg-slate-400 h-3 rounded-full" style={{width: '50%'}}></div>
            </div>
          </div>
          
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Top Quartile</span>
              <span>89th percentile</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3">
              <div className="bg-emerald-600 h-3 rounded-full" style={{width: '89%'}}></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Efficiency Score Gauge */}
      <div className="bg-slate-100 rounded-lg p-6 text-center">
        <h3 className="font-bold text-slate-800 mb-2">Efficiency Score</h3>
        <div className="text-4xl font-bold text-emerald-600 mb-2">{data.efficiency_score}/100</div>
        <p className="text-sm text-slate-600">
          Your facility is positioned to move from median to top quartile within 18–24 months.
        </p>
      </div>
    </div>
  </div>
);

// Implementation Roadmap Tile Component
const ImplementationRoadmapTile = ({ data }) => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden">
    <div className="bg-slate-800 text-white p-6">
      <div className="flex items-center">
        <span className="text-2xl mr-3">🗓️</span>
        <h2 className="text-xl font-bold">Implementation Roadmap</h2>
      </div>
    </div>
    
    <div className="p-6">
      <div className="space-y-4">
        {/* Phase 1 */}
        <div className="bg-emerald-100 rounded-lg p-4 border-l-4 border-emerald-600">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <h3 className="font-bold text-emerald-800">Phase 1: Quick Wins</h3>
              <p className="text-sm text-slate-600">0-6 months</p>
            </div>
            <div className="text-center">
              <div className="font-bold text-slate-800">8</div>
              <div className="text-sm text-slate-600">Measures</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-emerald-600">$715K</div>
              <div className="text-sm text-slate-600">Savings</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-slate-800">1,100%</div>
              <div className="text-sm text-slate-600">ROI</div>
            </div>
          </div>
        </div>
        
        {/* Phase 2 */}
        <div className="bg-slate-100 rounded-lg p-4 border-l-4 border-slate-400">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <h3 className="font-bold text-slate-800">Phase 2: Strategic</h3>
              <p className="text-sm text-slate-600">6-18 months</p>
            </div>
            <div className="text-center">
              <div className="font-bold text-slate-800">4</div>
              <div className="text-sm text-slate-600">Measures</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-emerald-600">$285K</div>
              <div className="text-sm text-slate-600">Savings</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-slate-800">335%</div>
              <div className="text-sm text-slate-600">ROI</div>
            </div>
          </div>
        </div>
        
        {/* Phase 3 */}
        <div className="bg-blue-100 rounded-lg p-4 border-l-4 border-blue-600">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <h3 className="font-bold text-blue-800">Phase 3: Advanced</h3>
              <p className="text-sm text-slate-600">18-36 months</p>
            </div>
            <div className="text-center">
              <div className="font-bold text-slate-800">3</div>
              <div className="text-sm text-slate-600">Measures</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-emerald-600">$172K</div>
              <div className="text-sm text-slate-600">Savings</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-slate-800">181%</div>
              <div className="text-sm text-slate-600">ROI</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// LEAP CTA Tile Component
const LEAPCTATile = ({ data }) => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden">
    <div className="bg-slate-800 text-white p-6">
      <div className="flex items-center">
        <span className="text-2xl mr-3">🚀</span>
        <h2 className="text-xl font-bold">LEAP Program Opportunity</h2>
      </div>
    </div>
    
    <div className="p-6">
      {/* Two-column comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-slate-100 rounded-lg p-4">
          <h3 className="font-bold text-slate-800 mb-3">ESA Report ($990)</h3>
          <ul className="space-y-2 text-sm text-slate-600">
            <li>✓ Savings Identified</li>
            <li>✓ ROI Analysis</li>
            <li>✓ Strategic Roadmap</li>
            <li>✓ One-Time Assessment</li>
          </ul>
        </div>
        
        <div className="bg-emerald-100 rounded-lg p-4 border-2 border-emerald-600">
          <h3 className="font-bold text-emerald-800 mb-3">LEAP Program ($15K–$50K)</h3>
          <ul className="space-y-2 text-sm text-emerald-700">
            <li>✓ Savings Realized & Guaranteed</li>
            <li>✓ Performance Contracting</li>
            <li>✓ Full Implementation Partnership</li>
            <li>✓ Continuous Optimization</li>
          </ul>
        </div>
      </div>
      
      {/* CTA Banner */}
      <div className="bg-emerald-600 text-white rounded-lg p-6 text-center">
        <h3 className="text-xl font-bold mb-2">Next Step: Schedule Your Strategic Consultation</h3>
        <p className="mb-4">Based on your ${data.total_annual_savings.toLocaleString()} savings opportunity</p>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button className="bg-white text-emerald-600 px-6 py-2 rounded-lg font-bold hover:bg-slate-100 transition-colors">
            📧 leap@relyability.ai
          </button>
          <button className="bg-white text-emerald-600 px-6 py-2 rounded-lg font-bold hover:bg-slate-100 transition-colors">
            📞 1-800-RELY-ENERGY
          </button>
        </div>
        
        <div className="mt-4 text-emerald-100 text-sm">
          🔗 QR Code: Scan for instant consultation booking
        </div>
      </div>
    </div>
  </div>
);

// Methodology Tile Component
const MethodologyTile = ({ data }) => (
  <div className="bg-slate-100 rounded-xl shadow-lg overflow-hidden">
    <div className="bg-slate-700 text-white p-6">
      <div className="flex items-center">
        <span className="text-2xl mr-3">📚</span>
        <h2 className="text-xl font-bold">Methodology & Assumptions</h2>
      </div>
    </div>
    
    <div className="p-6 bg-slate-100">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="font-bold text-slate-800 mb-3">Data Sources</h3>
          <ul className="space-y-1 text-sm text-slate-700">
            <li>• U.S. Department of Energy Industrial Assessment Center Database</li>
            <li>• Rely Ability Statistical Analysis Engine</li>
            <li>• Industry Energy Performance Benchmarks</li>
          </ul>
        </div>
        
        <div>
          <h3 className="font-bold text-slate-800 mb-3">Key Calculations</h3>
          <div className="space-y-2 text-sm text-slate-700">
            <p><strong>Payback:</strong> Investment ÷ Annual Savings</p>
            <p><strong>ROI:</strong> (Annual Savings ÷ Investment) × 100</p>
            <p><strong>Currency:</strong> AUD, Energy: kWh</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default ESATileDashboard;